import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { NavigationExtras, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { AggregatedStatus, GatewaySearch } from '@vi-iot/client';
import { ViIconComponent, ViLoaderComponent, ViPanelComponent, ViToastService } from '@vi-iot/ui';

import {
  GatewayViewModel,
  SearchType,
  UserDetailsStore,
  userGatewaysInfiniteScrollLimit,
} from '@store';

import { CustomTranslationsService } from '../../../operational-data-common/translations';
import { GatewayInvitationListComponent } from './gateway-invitation-list/gateway-invitation-list.component';

import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BannerComponent } from '@components';
import { TranslateAggregatedStatusPipe } from 'src/app/operational-data-common/translation-pipes/translate-aggregated-status.pipe';
import { InfiniteScrollComponent } from 'src/app/shared/components/infinite-scroll/infinite-scroll.component';
import { CommonStandaloneModule } from 'src/app/shared/common-standalone.module';

@Component({
  selector: 'app-user-gateway-list',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    ScrollingModule,
    ViPanelComponent,
    ViIconComponent,
    ViLoaderComponent,
    InfiniteScrollComponent,
    MatIconModule,
    MatTooltipModule,
    TranslateAggregatedStatusPipe,
    BannerComponent,
    MatButtonModule,
  ],
  templateUrl: './user-gateway-list.component.html',
  styleUrls: ['./user-gateway-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserGatewayListComponent implements OnInit {
  @Input()
  user: any;

  @Input()
  userDetails: any;

  _gatewaysVM: GatewayViewModel;

  get gatewaysVM(): GatewayViewModel {
    return this._gatewaysVM;
  }

  @Input()
  set gatewaysVM(value: GatewayViewModel) {
    this._gatewaysVM = value;
    this.gatewaysState$.next({
      gateways: value.gateways,
      cursor: value.cursor,
      loading: false,
      loaded: true,
    });

    if (value.error) {
      if (value.error.statusCode === 403) {
        this.showNoPermissionInfo = true;
      } else {
        this.showNoPermissionInfo = false;

        this.viToastService.error({
          message: `${this.translate.instant('+USER_DETAILS.GATEWAYS.SNACKBAR.ERROR_MESSAGE')} ${
            value.error.statusCode
          } - ${value.error.errorType}`,
        });
      }
    }

    if (!value.isLoading) {
      setTimeout(() => {
        // setTimeout is needed here to allow render the view port first (with the scroll buffer at the bottom).
        // Then we can decrease the height. Otherwise the view port adds extra height as a buffer to our height.
        this.renderer.setStyle(
          this.viewPort.elementRef.nativeElement,
          'height',
          this.viewportHeight
        );
        this.renderer.setStyle(
          this.viewPort.elementRef.nativeElement,
          'overflow',
          this.viewportOverflow
        );
      }, 50);
    }
  }

  @ViewChild(CdkVirtualScrollViewport, { static: true }) viewPort: CdkVirtualScrollViewport;

  itemSize = 48;
  limit = userGatewaysInfiniteScrollLimit;
  nextBatch = false;
  userName = '';
  aggregatedStatus = AggregatedStatus;
  showNoPermissionInfo = false;
  coldSystemTooltip: string;

  type$: BehaviorSubject<SearchType> = new BehaviorSubject<SearchType>(SearchType.gateway);
  gatewaysState$: Subject<any> = new Subject<any>();

  constructor(
    private userDetailsStore: UserDetailsStore,
    private viToastService: ViToastService,
    private translate: TranslateService,
    private renderer: Renderer2,
    private router: Router,
    private dialog: MatDialog,
    private customTranslationsService: CustomTranslationsService
  ) {}

  ngOnInit(): void {
    this.userName = `${this.user.firstName} ${this.user.lastName}`;
    this.coldSystemTooltip = this.customTranslationsService.INSTALLATION_STATUS_COLD_SYSTEM;
  }

  onNextBatchChange($event: boolean): void {
    this.nextBatch = $event;
  }

  onFetchNext(): any {
    if (this.gatewaysVM.cursor) {
      this.userDetailsStore.getUserInstallations(
        this.limit,
        this.gatewaysVM.cursor,
        this.userDetails
      );
    }
  }

  navigateToInstallation(gateway: GatewaySearch): void {
    const navigationExtras: NavigationExtras = gateway.serial
      ? { queryParams: { gatewaySerial: gateway.serial } }
      : {};
    this.router.navigate([`/installations/${gateway.installationId}`], navigationExtras);
  }

  showAllInvitations(): void {
    this.dialog.open(GatewayInvitationListComponent, {
      width: '800px',
      panelClass: 'gateway-invitation-list-dialog',
      disableClose: true,
      data: {
        customerNumber: this.user.customerNumber,
      },
    });
  }

  get viewportHeight(): string {
    const len = this.gatewaysVM?.gateways?.length || 0;
    const height = len < 7 ? len * this.itemSize : 340;

    return `${height}px`;
  }

  get viewportOverflow(): string {
    const len = this.gatewaysVM?.gateways?.length || 0;

    return len > 7 ? 'auto' : 'initial';
  }
}
